<script setup lang="ts">
import { type MaybeRef } from 'vue'
import UiAsyncCombobox from './UiAsyncCombobox/index.vue'
import UiIcon from './UiIcon.vue'
import type { ComboboxGroupData, ComboboxItemData } from './types/combobox'

export interface UiPredictiveSearchComboboxProps {
    id?: string
    name?: string
    showSearchIcon?: boolean
    placeholder?: string
    disabled?: boolean
    size?: 'md' | 'lg'
    mode?: 'preserve' | 'clearOnSelect'
    hideComboboxWhenEmpty?: boolean
    multiple?: boolean
    data: ComboboxGroupData[] | ComboboxItemData[]
    isFetching: boolean
    isError?: boolean
    error?: Error | string | null
    invalid?: boolean
    hint?: string
    onValidate?: () => void
}

const props = withDefaults(defineProps<UiPredictiveSearchComboboxProps>(), {
    size: 'lg',
    placeholder: 'Search...',
    showSearchIcon: true,
    invalid: false,
    mode: 'clearOnSelect',
    hideComboboxWhenEmpty: false,
    multiple: false,
})

const model = defineModel<string | undefined>()
const selectedId = defineModel<string | string[] | undefined>('selectedId', { required: false })

const emit = defineEmits<{
    (e: 'select', value: { value: string; label: string; metadata?: Record<string, unknown> | Record<string, unknown> }): void
    (e: 'validate'): void
}>()

function handleSelect(value: { value: string | string[]; label: string | string[]; metadata?: Record<string, unknown> | Record<string, unknown>[] }) {
    const selectedValue = Array.isArray(value.value) ? value.value[0] : value.value
    const selectedLabel = Array.isArray(value.label) ? value.label[0] : value.label
    const selectedMetadata = Array.isArray(value.metadata) ? value.metadata[0] : value.metadata
    emit('select', { value: selectedValue, label: selectedLabel, metadata: selectedMetadata })
}
</script>

<template>
    <UiAsyncCombobox
        v-bind="$props"
        v-model="model"
        v-model:selectedId="selectedId"
        displayMode="inline"
        @select="handleSelect"
        @validate="emit('validate')"
    >
        <template #leading-icon v-if="props.showSearchIcon">
            <UiIcon name="search" class="w-6 h-6 text-se-gray-700" />
        </template>
        
        <template #group-heading="slotProps">
            <slot name="group-heading" v-bind="slotProps" />
        </template>
        
        <template #loading>
            <slot name="loading" />
        </template>
        
        <template #empty>
            <slot name="empty" />
        </template>
    </UiAsyncCombobox>
</template>
